import { useQuery } from "@tanstack/react-query";
import { type MetaAdsGeoTargeting, metaAdsLocationTypesForApi } from "shared";
import { useDebounceValue } from "usehooks-ts";
import { Api } from "../api.config";
import { useCompany } from "../company/useCompany";
import { QueryKeys } from "../LinkGen";

export function useDeliveryEstimate(
	geo: MetaAdsGeoTargeting | undefined,
	optimizationGoal: string,
	ageMin: number,
	ageMax: number,
	genders: number[],
) {
	const { id: companyId } = useCompany();

	const targetingSpec = buildTargetingSpec(geo, ageMin, ageMax, genders);
	const geoLocations = (targetingSpec as { geo_locations?: object })
		.geo_locations;
	const enabled = !!geoLocations && Object.keys(geoLocations).length > 0;

	const specKey = JSON.stringify(targetingSpec);
	const [debouncedSpecKey] = useDebounceValue(specKey, 400);

	return useQuery({
		queryKey: [
			...QueryKeys.Integrations.MetaAds.DeliveryEstimate(companyId),
			debouncedSpecKey,
			optimizationGoal,
		],
		queryFn: async () => {
			const res = await Api.GetMetaAdsDeliveryEstimate({
				targetingSpec: JSON.parse(debouncedSpecKey) as object,
				optimizationGoal,
			});
			return res.data;
		},
		enabled,
		staleTime: 30_000,
		refetchOnWindowFocus: false,
	});
}

function buildTargetingSpec(
	geo: MetaAdsGeoTargeting | undefined,
	ageMin: number,
	ageMax: number,
	genders: number[],
): object {
	const geoLocations: Record<string, unknown> = {};
	const mode = geo?.mode ?? "radius_pin";

	if (
		mode === "radius_pin" &&
		geo?.locationPins &&
		geo.locationPins.length > 0
	) {
		const validPins = geo.locationPins.filter(
			(pin) =>
				pin.latitude >= -90 &&
				pin.latitude <= 90 &&
				pin.longitude >= -180 &&
				pin.longitude <= 180 &&
				pin.radius > 0,
		);
		if (validPins.length > 0) {
			geoLocations.custom_locations = validPins.map((pin) => ({
				latitude: pin.latitude,
				longitude: pin.longitude,
				radius: pin.radius,
				distance_unit: pin.distanceUnit,
			}));
		}
	} else if (mode === "zip_code" && geo?.zipCodes && geo.zipCodes.length > 0) {
		geoLocations.zips = geo.zipCodes.map((z) => ({ key: z.key }));
	} else if (mode === "city_region") {
		if (geo?.cities && geo.cities.length > 0) {
			geoLocations.cities = geo.cities.map((c) => ({ key: c.key }));
		}
		if (geo?.regions && geo.regions.length > 0) {
			geoLocations.regions = geo.regions.map((r) => ({ key: r.key }));
		}
		if (geo?.dmas && geo.dmas.length > 0) {
			geoLocations.geo_markets = geo.dmas.map((d) => ({ key: d.key }));
		}
	} else if (geo?.countries && geo.countries.length > 0) {
		geoLocations.countries = geo.countries;
	}

	if (Object.keys(geoLocations).length > 0) {
		geoLocations.location_types = metaAdsLocationTypesForApi(geo?.locationType);
	}

	const spec: Record<string, unknown> = {
		geo_locations: geoLocations,
		age_min: ageMin,
		age_max: ageMax,
		targeting_automation: { advantage_audience: 0 },
	};

	if (genders.length > 0 && !genders.includes(0)) {
		spec.genders = genders;
	}

	if (geo?.exclusions) {
		const excluded: Record<string, unknown> = {};
		if (geo.exclusions.zipCodes?.length) {
			excluded.zips = geo.exclusions.zipCodes.map((z) => ({ key: z.key }));
		}
		if (geo.exclusions.cities?.length) {
			excluded.cities = geo.exclusions.cities.map((c) => ({
				key: c.key,
			}));
		}
		if (geo.exclusions.regions?.length) {
			excluded.regions = geo.exclusions.regions.map((r) => ({
				key: r.key,
			}));
		}
		if (geo.exclusions.customLocations?.length) {
			excluded.custom_locations = geo.exclusions.customLocations.map((pin) => ({
				latitude: pin.latitude,
				longitude: pin.longitude,
				radius: pin.radius,
				distance_unit: pin.distanceUnit,
			}));
		}
		if (Object.keys(excluded).length > 0) {
			spec.excluded_geo_locations = excluded;
		}
	}

	return spec;
}
